import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Departement, ArticlePrixDepartement } from '../models/models';
import api from './api';

// ── State ────────────────────────────────────────────────────────────────────

interface DepartementState {
  departements: Departement[];
  prixDepartement: ArticlePrixDepartement[];
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedDepartement: Departement | null;
}

const initialState: DepartementState = {
  departements: [],
  prixDepartement: [],
  loading: false,
  error: null,
  successMessage: null,
  selectedDepartement: null,
};

// ── Thunks : DEPARTEMENT ─────────────────────────────────────────────────────

export const fetchDepartements = createAsyncThunk<
  Departement[],
  void,
  { rejectValue: string }
>('departement/fetchAll', async (_, thunkAPI) => {
  try {
    const response = await api.get('/departements', {
      params: { visible: 1, limit: 1000, offset: 0 },
    });
    return response.data.data as Departement[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur chargement départements');
  }
});

export const createOrUpdateDepartement = createAsyncThunk<
  Departement,
  Departement,
  { rejectValue: string }
>('departement/createOrUpdate', async (data, thunkAPI) => {
  try {
    const response = await api.post('/departements', data);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur sauvegarde département');
  }
});

export const deleteDepartement = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('departement/delete', async (id, thunkAPI) => {
  try {
    await api.delete('/departements', { params: { id } });
    return id;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur suppression département');
  }
});

// ── Thunks : ARTICLE_PRIX_DEPARTEMENT ────────────────────────────────────────

// Récupère les prix de TOUS les départements pour un article (pas de filtre dept).
// Endpoint dédié /prix/all → la SP reçoit departement_id=null → retourne tout.
// À utiliser dans ArticlePrixDepartementForm uniquement.
export const fetchAllPrixDepartement = createAsyncThunk<
  ArticlePrixDepartement[],
  { article_id?: number },
  { rejectValue: string }
>('departement/fetchAllPrix', async (params, thunkAPI) => {
  try {
    const response = await api.get('/departements/prix/all', {
      params: { ...params, limit: 1000, offset: 0 },
    });
    return response.data.data as ArticlePrixDepartement[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur chargement prix tous départements');
  }
});

export const fetchPrixDepartement = createAsyncThunk<
  ArticlePrixDepartement[],
  { departement_id?: number; article_id?: number },
  { rejectValue: string }
>('departement/fetchPrix', async (params, thunkAPI) => {
  try {
    const response = await api.get('/departements/prix', {
      params: { ...params, limit: 1000, offset: 0 },
    });
    return response.data.data as ArticlePrixDepartement[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur chargement prix département');
  }
});

export const createOrUpdatePrixDepartement = createAsyncThunk<
  ArticlePrixDepartement,
  ArticlePrixDepartement,
  { rejectValue: string }
>('departement/createOrUpdatePrix', async (data, thunkAPI) => {
  try {
    const response = await api.post('/departements/prix', data);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur sauvegarde prix département');
  }
});

export const deletePrixDepartement = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('departement/deletePrix', async (id, thunkAPI) => {
  try {
    await api.delete('/departements/prix', { params: { id } });
    return id;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur suppression prix département');
  }
});

// ── Slice ────────────────────────────────────────────────────────────────────

const departementSlice = createSlice({
  name: 'departement',
  initialState,
  reducers: {
    setSelectedDepartement: (state, action: PayloadAction<Departement | null>) => {
      state.selectedDepartement = action.payload;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchDepartements
      .addCase(fetchDepartements.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchDepartements.fulfilled, (state, action) => {
        state.loading = false;
        state.departements = action.payload;
      })
      .addCase(fetchDepartements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // createOrUpdateDepartement
      .addCase(createOrUpdateDepartement.pending, (state) => { state.loading = true; state.error = null; state.successMessage = null; })
      .addCase(createOrUpdateDepartement.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Département enregistré !';
      })
      .addCase(createOrUpdateDepartement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteDepartement
      .addCase(deleteDepartement.pending, (state) => { state.loading = true; })
      .addCase(deleteDepartement.fulfilled, (state, action) => {
        state.loading = false;
        state.departements = state.departements.filter((d) => d.id !== action.payload);
        state.successMessage = 'Département supprimé.';
      })
      .addCase(deleteDepartement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchAllPrixDepartement (ArticlePrixDepartementForm) : tous les depts en mode ALL, le dept actif sinon
      .addCase(fetchAllPrixDepartement.pending, (state) => { state.loading = true; })
      .addCase(fetchAllPrixDepartement.fulfilled, (state, action) => {
        state.loading = false;
        state.prixDepartement = action.payload;
      })
      .addCase(fetchAllPrixDepartement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchPrixDepartement
      .addCase(fetchPrixDepartement.pending, (state) => { state.loading = true; })
      .addCase(fetchPrixDepartement.fulfilled, (state, action) => {
        state.loading = false;
        state.prixDepartement = action.payload;
      })
      .addCase(fetchPrixDepartement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // createOrUpdatePrixDepartement
      .addCase(createOrUpdatePrixDepartement.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createOrUpdatePrixDepartement.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Prix département enregistré !';
      })
      .addCase(createOrUpdatePrixDepartement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deletePrixDepartement
      .addCase(deletePrixDepartement.pending, (state) => { state.loading = true; })
      .addCase(deletePrixDepartement.fulfilled, (state, action) => {
        state.loading = false;
        state.prixDepartement = state.prixDepartement.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePrixDepartement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedDepartement, clearMessages } = departementSlice.actions;
export default departementSlice.reducer;
